import { useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { PbjPrediction, PbjSuggestedPlan } from "../../../utils/models";
import { buildPlanItem, calculateRecData } from "./recMath.utils";
import { addsBeforeCuts, planSpendFor, spendMapFromPlan, suggestedIdsFromPlan, wageFor } from "./recommendationSliders.utils";
import { type PlanItem, RECOMMENDATION_TYPES, type RecommendationType } from "./recommendations.constants";
import { planSeedKey } from "./trackedPlan.utils";

type Args = {
	prediction: PbjPrediction;
	census: number;
	selectedPlan: PlanItem[];
	onAddToPlan: (item: PlanItem) => void;
	onUpdatePlan: (item: PlanItem) => void;
	onRemoveFromPlan: (id: PlanItem["id"]) => void;
	onResetPlan: () => void;
};

export type RecommendationSliders = {
	// Levers with a real wage — the only ones the sidebar renders.
	visibleRecs: RecommendationType[];
	selectedById: Record<string, PlanItem>;
	spendMap: Record<string, number>;
	suggestedRecIds: Set<string>;
	// Bumped by every Suggest, Reset and re-seed: the sliders remount on it so the land pulse replays.
	suggestNonce: number;
	// Levers with a non-zero slider value worth committing — the "Add all" target set.
	valuedRecs: RecommendationType[];
	// Whether any lever can be cut at all (gates the "left to cut them" subheader).
	anyCuttable: boolean;
	handleSliderChange: (rec: RecommendationType, newSpend: number) => void;
	// One lever's Add: commits it to the Action Plan and records the add.
	handleAddLever: (item: PlanItem) => void;
	handleAddAll: () => void;
	handleReset: () => void;
	// Positions every non-locked slider from a suggested plan and marks the ones it moved as suggested.
	applySuggestedPlan: (plan: PbjSuggestedPlan, lockedIds: Set<string>) => void;
};

/**
 * The sidebar's slider state: where each lever's slider sits, which ones the latest suggestion
 * positioned, and the commits from sliders into the Action Plan (drag, Add all, Reset). A lever in
 * the plan renders from its plan item's spend; the rest render from the local `spendMap`.
 */
export const useRecommendationSliders = ({
	prediction,
	census,
	selectedPlan,
	onAddToPlan,
	onUpdatePlan,
	onRemoveFromPlan,
	onResetPlan,
}: Args): RecommendationSliders => {
	const { track } = useAnalytics();

	// Hide slabs whose wage came back null from the BE (no active hourly nurses of that role). The
	// slider math (`HPPD = spend / wage`) needs a real wage, and the product decision is to omit
	// rather than fall back to a fake number.
	const visibleRecs = useMemo(() => RECOMMENDATION_TYPES.filter((r) => prediction.deptWages[r.wageKey] !== null), [prediction.deptWages]);
	// Index selectedPlan by recommendation id once per render so the per-rec lookup is O(1). Also
	// lets the slider + labels reflect the BE-suggested spend / impact / points without FE math.
	const selectedById = useMemo<Record<string, PlanItem>>(() => Object.fromEntries(selectedPlan.map((p) => [p.id, p])), [selectedPlan]);

	// Sliders are pre-positioned on load from the prediction's pre-computed suggested plan (no
	// network — an empty lockedActions request reproduces it exactly).
	const [spendMap, setSpendMap] = useState<Record<string, number>>(() => spendMapFromPlan(prediction.suggestedPlan, visibleRecs));
	// Rec ids the current suggestion positioned with a non-zero spend — drives the "Suggested"
	// badge/tint + the one-shot slider land animation.
	const [suggestedRecIds, setSuggestedRecIds] = useState<Set<string>>(() => suggestedIdsFromPlan(prediction.suggestedPlan, visibleRecs));
	const [suggestNonce, setSuggestNonce] = useState(0);

	// Re-seed sliders AND the suggested-highlight set whenever the prediction they're seeded from changes
	// (Calculate / quarter / target change). Keyed on `planSeedKey` — not the object reference — so a
	// refetch that only moved the tracked plan (Track / Stop tracking) keeps the user's sliders, matching
	// the Action Plan, which re-seeds on the same key.
	const seedKey = useMemo(() => planSeedKey(prediction), [prediction]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-seed only when the seed inputs change, not on a refetch that only moved `trackedPlan`.
	useEffect(() => {
		setSpendMap(spendMapFromPlan(prediction.suggestedPlan, visibleRecs));
		setSuggestedRecIds(suggestedIdsFromPlan(prediction.suggestedPlan, visibleRecs));
	}, [seedKey]);

	const applySuggestedPlan = useCallback(
		(plan: PbjSuggestedPlan, lockedIds: Set<string>) => {
			const stepByAction = new Map(plan.steps.map((s) => [s.actionId, s]));
			const positioned = new Set<string>();
			setSpendMap((prev) => {
				const next = { ...prev };
				for (const rec of visibleRecs) {
					// Locked recs render from their plan item's spend — never touch their slider.
					if (lockedIds.has(rec.id)) continue;
					const spend = planSpendFor(stepByAction, rec.id);
					next[rec.id] = spend;
					if (spend !== 0) positioned.add(rec.id);
				}
				return next;
			});
			// Locked levers keep whatever suggested standing they had because Suggest did not touch them.
			setSuggestedRecIds((prev) => new Set([...positioned, ...[...prev].filter((id) => lockedIds.has(id))]));
			setSuggestNonce((n) => n + 1);
		},
		[visibleRecs],
	);

	const handleSliderChange = useCallback(
		(rec: RecommendationType, newSpend: number) => {
			setSpendMap((prev) => ({ ...prev, [rec.id]: newSpend }));
			// A manual drag means the user is no longer looking at the suggested value — drop the
			// "Suggested" affordance (badge/animation) for this rec.
			setSuggestedRecIds((prev) => {
				if (!prev.has(rec.id)) return prev;
				const next = new Set(prev);
				next.delete(rec.id);
				return next;
			});
			if (!selectedById[rec.id]) return;
			const data = calculateRecData(rec, newSpend, wageFor(rec, prediction), prediction, census);
			// Same guard as the row's Add button: a cut under one whole hour a day commits nothing, so an
			// in-plan lever dragged there leaves the plan instead of lingering as "Cut 0 hours" (which would
			// still be priced, and which the tracker would store as an infeasible 0-hour goal). The slider
			// keeps its position; dragging past an hour lets the user Add it again.
			if (newSpend < 0 && data.hours === 0) {
				onRemoveFromPlan(rec.id);
				return;
			}
			// The per-category `hppdGap` is baseline-independent (hours/census/days/scale only), so storing
			// it now is correct and order-independent. ActionItems re-scores the marginal points from it.
			onUpdatePlan(buildPlanItem(rec, newSpend, data));
		},
		[census, onRemoveFromPlan, onUpdatePlan, prediction, selectedById],
	);

	// The live spend each rec's slider shows — centralized so "Add all" pushes exactly what the user sees.
	// DEFAULT: a lever that became visible with a new prediction renders once before the re-seed effect
	// above runs; 0 is exactly what that effect seeds it to.
	const currentSpendFor = useCallback((recId: string): number => selectedById[recId]?.spend ?? spendMap[recId] ?? 0, [selectedById, spendMap]);

	// Every visible rec whose slider carries a non-zero value (suggested, seeded or hand-tuned, and
	// INCLUDING ones already in the plan). A cut too small to round to one whole hour commits nothing
	// (its row's Add is disabled too), so it's left out rather than pushed as a "Cut 0 hours" item.
	const valuedRecs = useMemo(
		() =>
			visibleRecs.filter((r) => {
				const spend = currentSpendFor(r.id);
				if (spend === 0) return false;
				if (spend > 0) return true;
				return calculateRecData(r, spend, wageFor(r, prediction), prediction, census).hours !== 0;
			}),
		[visibleRecs, currentSpendFor, prediction, census],
	);

	// The subheader's "left to cut them" is only a true promise when some lever can be cut (a quarter
	// that hasn't started, or no worked hours, leaves every slider add-only).
	const anyCuttable = visibleRecs.some((r) => {
		const limit = prediction.cutLimits[r.id];
		return limit !== undefined && limit.maxCutHoursPerDay > 0;
	});
	const handleAddLever = useCallback(
		(item: PlanItem) => {
			track("pbj_model_lever_added", { department_id: prediction.config.departmentId, lever_id: item.id, direction: item.spend < 0 ? "cut" : "add" });
			onAddToPlan(item);
		},
		[onAddToPlan, prediction.config.departmentId, track],
	);

	// Pushes the CURRENT slider positions into the Action Plan in one click, built with the same FE
	// math as the per-rec "Add". In-plan recs are re-synced (update); the rest are added, adds before cuts.
	const handleAddAll = useCallback(() => {
		const ordered = addsBeforeCuts(valuedRecs, currentSpendFor);
		const cutCount = ordered.filter((r) => currentSpendFor(r.id) < 0).length;
		track("pbj_model_add_all_clicked", {
			department_id: prediction.config.departmentId,
			count: ordered.length,
			add_count: ordered.length - cutCount,
			cut_count: cutCount,
		});
		for (const rec of ordered) {
			const spend = currentSpendFor(rec.id);
			const item = buildPlanItem(rec, spend, calculateRecData(rec, spend, wageFor(rec, prediction), prediction, census));
			if (selectedById[rec.id]) onUpdatePlan(item);
			else onAddToPlan(item);
		}
	}, [census, currentSpendFor, onAddToPlan, onUpdatePlan, selectedById, valuedRecs, prediction, track]);

	// Restores the post-refresh default: re-seed sliders + highlight from the pre-computed plan and
	// reset the parent's Action Plan the same way (equivalent to reloading the page).
	const handleReset = useCallback(() => {
		track("pbj_model_reset_plan_clicked", { department_id: prediction.config.departmentId });
		setSpendMap(spendMapFromPlan(prediction.suggestedPlan, visibleRecs));
		setSuggestedRecIds(suggestedIdsFromPlan(prediction.suggestedPlan, visibleRecs));
		setSuggestNonce((n) => n + 1);
		onResetPlan();
	}, [onResetPlan, prediction.config.departmentId, prediction.suggestedPlan, visibleRecs, track]);

	return {
		visibleRecs,
		selectedById,
		spendMap,
		suggestedRecIds,
		suggestNonce,
		valuedRecs,
		anyCuttable,
		handleSliderChange,
		handleAddLever,
		handleAddAll,
		handleReset,
		applySuggestedPlan,
	};
};
